<template>
    <form @change="isFormDirty" @submit.prevent="handleSubmit" class="form-container" data-cy="form-container">
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section">
            <div class="section-body" data-cy="section-body">
                <div class="input-container mb-2 w-full px-4" data-cy="policy-holder-national-id-field">
                    <ui-label>{{ $t('covers.forms.policy_holder.national_id') }} </ui-label>
                    <div class="relative">
                        <a-auto-complete
                            v-model:value="v$.policy_holder.national_id.$model"
                            @search="handleSearch"
                            @select="selectTrn"
                            @blur="handleBlur"
                            :options="policy_names"
                            :class="{
                                'form-error':
                                    v$.policy_holder.national_id.$error &&
                                    v$.policy_holder.national_id.required.$invalid,
                            }"
                        />
                    </div>
                    <div
                        data-cy="field-policy-holder-national-id-error"
                        :class="{
                            'text-default-red':
                                v$.policy_holder.national_id.$error && v$.policy_holder.national_id.required.$invalid,
                        }"
                        v-if="v$.policy_holder.national_id.$error && v$.policy_holder.national_id.required.$invalid"
                    >
                        {{ $t('validation.required') }}
                    </div>
                </div>
                <div class="input-container mb-2 w-full px-4" data-cy="policy-holder-name-field">
                    <ui-label>{{ $t('covers.forms.policy_holder.name') }}</ui-label>
                    <ui-input
                        v-model="v$.policy_holder.name.$model"
                        :class="{ 'form-error': v$.policy_holder.name.$error }"
                        :formatter="formatters.titleCaseFormatter"
                    />
                    <div
                        data-cy="field-policy-holder-name-error"
                        :class="{ 'text-default-red': v$.policy_holder.name.$error }"
                        v-if="v$.policy_holder.name.required.$invalid && v$.policy_holder.name.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                </div>
            </div>
            <div class="section-body">
                <div data-cy="policy-holder-is-a-policy-holder-field" class="input-container lg:w-1/2 px-4">
                    <ui-checkbox :disabled="!is_driver" v-model="policy_holder.is_a_policy_holder">
                        {{ $t('covers.forms.policy_holder.is_a_policy_holder') }}
                    </ui-checkbox>
                </div>
                <div data-cy="policy-holder-is-driver-field" class="input-container lg:w-1/2 px-4">
                    <ui-checkbox :disabled="is_driver" v-model="policy_holder.is_a_driver">
                        {{ $t('covers.forms.policy_holder.is_a_driver') }}
                    </ui-checkbox>
                </div>
                <div
                    v-if="isDriverEnabled"
                    data-cy="policy-holder-is-main-driver-field"
                    class="input-container lg:w-1/2 px-4"
                >
                    <ui-checkbox :disabled="policy_holder.is_excluded" v-model="policy_holder.is_main_driver">
                        {{ $t('covers.forms.policy_holder.is_main_driver') }}
                    </ui-checkbox>
                </div>
                <div
                    v-if="isDriverEnabled"
                    data-cy="policy-holder-is-excluded-field"
                    class="input-container lg:w-1/2 px-4"
                >
                    <ui-checkbox :disabled="policy_holder.is_main_driver" v-model="policy_holder.is_excluded">
                        {{ $t('covers.forms.policy_holder.is_excluded') }}
                    </ui-checkbox>
                </div>
            </div>
        </div>
        <div class="drawer-footer">
            <slot name="actions"></slot>
            <a-button data-cy="drawer-cancel-button" @click="onDrawerClose" class="btn-cancel" :disabled="processing">
                {{ $t('covers.forms.policy_holder.buttons.cancel') }}
            </a-button>
            <ui-submit-button data-cy="drawer-save-btn">
                <span v-if="!processing">{{ $t('covers.forms.policy_holder.buttons.save') }}</span>
                <span v-else>{{ $t('covers.forms.policy_holder.buttons.saving') }}</span>
            </ui-submit-button>
        </div>
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import formatters from '@/utils/formatters.js';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiInput,
        UiCheckbox,
        UiLabel,
        UiSubmitButton,
        ValidationErrors,
    },
    computed: {
        isDriverEnabled() {
            if (!this.policy_holder.is_a_driver) {
                this.policy_holder.is_main_driver = false;
                this.policy_holder.is_excluded = false;
                return false;
            }
            return true;
        },
    },
    data() {
        return {
            loading: false,
            formatters,
            policy_names: [],
        };
    },
    validations: {
        policy_holder: {
            id: {},
            name: { required },
            national_id: { required },
            is_a_driver: {},
            is_main_driver: {},
            is_excluded: {},
        },
    },
    props: {
        policy_holder: Object,
        validation: Object,
        processing: Boolean,
        is_driver: {
            type: Boolean,
            default: false,
        },
        insurer_id: String,
        intermediary_id: String,
    },
    methods: {
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            this.v$.$reset();
            this.$emit('formSubmit', this.policy_holder);
        },
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
        selectTrn(value) {
            this.handleSearch(value);
            this.policy_names.forEach((policy_name) => {
                if (policy_name.value == value) {
                    this.v$.policy_holder.name.$model = policy_name.text;
                }
            });
        },
        handleBlur(value) {
            let error = this.v$.policy_holder.name.$errors.length;
            if (this.policy_names.length == 1) {
                if (this.policy_holder.national_id == this.policy_names[0].value) {
                    this.v$.policy_holder.name.$model = this.policy_names[0].text;
                } else {
                    this.v$.policy_holder.name.$model = '';
                    if (!error) {
                        this.v$.policy_holder.name.$reset();
                    }
                }
            } else {
                this.v$.policy_holder.name.$model = '';
                if (!error) {
                    this.v$.policy_holder.name.$reset();
                }
            }
        },
        handleSearch(value) {
            let policy_names = [];

            value = this.formatters.numberFormatter(value);

            if (value != '' && this.insurer_id !== '' && this.intermediary_id !== '') {
                this.$http
                    .post('/api/policy_names/get-by-trn/' + value, {
                        insurer_id: this.insurer_id,
                        intermediary_id: this.intermediary_id,
                    })
                    .then((response) => {
                        response.data.policy_names.forEach((record) => {
                            policy_names.push({
                                text: record.name,
                                value: record.national_id,
                                label: record.name + ' - ' + record.national_id,
                            });
                        });
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVSH02' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.policy_names = policy_names;
                    });
            }
        },
    },
};
</script>
